// Which languages are allowed into Google's index, in one place.
//
// The catalogue (club, region and theme pages) exists in six languages, but
// only the English text is written by hand; the other five are machine
// translations (tools/translate_clubs.py). After the August 2026 spam update
// the site was demoted sitewide, and 4,000-odd machine-translated pages on a
// four-week-old domain are the most likely reason. Italian and Spanish are
// kept: together they brought 16% of the impressions before the drop, and
// they are the two languages a native reader is going to check first. Dutch,
// German and French stay online and fully usable, they just carry noindex and
// are left out of the sitemap and of every hreflang set (a noindex page has no
// business in one).
//
// Reversible by editing INDEXABLE_LOCALES. Nothing else needs to change: the
// routes, the sitemap and hreflangLinks all read this list.
//
// The core pages (home, /clubs, /partner, /faq, /guides) are a different case
// and stay indexable in all six languages: their text comes from the
// hand-kept UI dictionaries in i18n.tsx, there are 25 of them in total, and
// they are the entry points the language switcher lands on.
import type { Lang } from "./i18n";

export const INDEXABLE_LOCALES: readonly Lang[] = ["en", "it", "es"];

// The localized languages that get catalogue URLs in the sitemap and in
// hreflang: every indexable locale except the unprefixed English source.
export const INDEXABLE_ALTERNATES: readonly Lang[] = INDEXABLE_LOCALES.filter((l) => l !== "en");

// Undefined means the unprefixed English route.
export function isIndexableLocale(lang: string | undefined): boolean {
  return (INDEXABLE_LOCALES as readonly string[]).includes(lang ?? "en");
}

// Path prefixes of the catalogue: pages that exist per language as a
// translation of the same English record.
const CATALOGUE_SEGMENTS = new Set(["club", "region", "theme"]);

// True for a localized catalogue path whose language is not indexable, e.g.
// /de/club/lido-blase or /fr/theme/with-pool/sicily. Core pages and English
// paths are never affected. Used by the sitemap filter and by the tests; the
// routes themselves decide via isIndexableLocale on their own locale param.
export function isNoindexCataloguePath(pathname: string): boolean {
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length < 2) return false;
  const [locale, segment] = parts;
  if (!/^[a-z]{2}$/.test(locale)) return false;
  if (!CATALOGUE_SEGMENTS.has(segment)) return false;
  return !isIndexableLocale(locale);
}

// The meta tag the routes emit. "follow" on purpose: the links on a German
// club page still lead to indexable English, Italian and Spanish pages.
export const NOINDEX_META = { name: "robots", content: "noindex,follow" } as const;

// A region page below this many clubs is a heading and a couple of cards, the
// same shape as the theme x region pages the MIN_CLUBS_PER_THEME_PAGE
// threshold already keeps out. Such a region stays online (it is a real place
// on the map with real links) but carries noindex until it has enough clubs.
// Live count on 22 Sep 2026 (public region pages): 34 of 35 regions have 5 or
// more clubs, the smallest being Montenegro (5) and Albania (6); the one below
// the line is Mauritius with 4. Regions with zero clubs are a 404 already.
export const MIN_CLUBS_PER_REGION_INDEX = 5;

export function regionIsIndexable(clubCount: number): boolean {
  return clubCount >= MIN_CLUBS_PER_REGION_INDEX;
}
